import { LETTER_SOUNDS } from './audio';

export type Letter = { upper: string; lower: string; name: string; sound: string };

// Insertion order below IS the Spanish abecedario order (incl. Ñ) — both
// LetrasView (one card per letter) and SilabasView (one card per letter+
// vowel combo) iterate this array directly, so reordering it reorders both.
const NAMES: Record<string, string> = {
  a: 'a', b: 'be', c: 'ce', d: 'de', e: 'e', f: 'efe', g: 'ge', h: 'hache',
  i: 'i', j: 'jota', k: 'ka', l: 'ele', m: 'eme', n: 'ene', ñ: 'eñe', o: 'o',
  p: 'pe', q: 'cu', r: 'erre', s: 'ese', t: 'te', u: 'u', v: 've',
  w: 'doble ve', x: 'equis', y: 'ye', z: 'zeta'
};

export const ALPHABET: Letter[] = Object.keys(NAMES).map((lower) => ({
  upper: lower.toUpperCase(),
  lower,
  name: NAMES[lower],
  sound: LETTER_SOUNDS[lower] ?? lower
}));

export const VOWELS = ['A', 'E', 'I', 'O', 'U'] as const;

export type LetterAnchor = {
  letter: string;
  word: string;
  emoji: string;
};

export const LETTER_ANCHORS: Record<string, LetterAnchor> = {
  a: { letter: 'A', word: 'Auto', emoji: '🚗' },
  b: { letter: 'B', word: 'Barco', emoji: '⛵' },
  c: { letter: 'C', word: 'Casa', emoji: '🏠' },
  d: { letter: 'D', word: 'Dado', emoji: '🎲' },
  e: { letter: 'E', word: 'Estrella', emoji: '⭐' },
  f: { letter: 'F', word: 'Flor', emoji: '🌸' },
  g: { letter: 'G', word: 'Gato', emoji: '🐱' },
  h: { letter: 'H', word: 'Helado', emoji: '🍦' },
  i: { letter: 'I', word: 'Isla', emoji: '🏝️' },
  j: { letter: 'J', word: 'Jirafa', emoji: '🦒' },
  k: { letter: 'K', word: 'Koala', emoji: '🐨' },
  l: { letter: 'L', word: 'Luna', emoji: '🌙' },
  m: { letter: 'M', word: 'Manzana', emoji: '🍎' },
  n: { letter: 'N', word: 'Nube', emoji: '☁️' },
  ñ: { letter: 'Ñ', word: 'Ñandú', emoji: '🦤' },
  o: { letter: 'O', word: 'Oso', emoji: '🐻' },
  p: { letter: 'P', word: 'Pelota', emoji: '⚽' },
  q: { letter: 'Q', word: 'Queso', emoji: '🧀' },
  r: { letter: 'R', word: 'Ratón', emoji: '🐭' },
  s: { letter: 'S', word: 'Sol', emoji: '☀️' },
  t: { letter: 'T', word: 'Tren', emoji: '🚂' },
  u: { letter: 'U', word: 'Uva', emoji: '🍇' },
  v: { letter: 'V', word: 'Vaca', emoji: '🐮' },
  w: { letter: 'W', word: 'Waffle', emoji: '🧇' },
  x: { letter: 'X', word: 'Xilófono', emoji: '🎶' },
  y: { letter: 'Y', word: 'Yate', emoji: '⛵' },
  z: { letter: 'Z', word: 'Zapato', emoji: '👟' }
};

export const SYLLABLE_ANCHORS: Record<string, { word: string; emoji: string }> = {
  MA: { word: 'Mamá', emoji: '👩' },
  ME: { word: 'Mesa', emoji: '🪑' },
  MI: { word: 'Miel', emoji: '🍯' },
  MO: { word: 'Mono', emoji: '🐵' },
  MU: { word: 'Muñeca', emoji: '🧸' },
  PA: { word: 'Papá', emoji: '👨' },
  PE: { word: 'Pelota', emoji: '⚽' },
  PI: { word: 'Pino', emoji: '🌲' },
  PO: { word: 'Pollo', emoji: '🐥' },
  PU: { word: 'Puerta', emoji: '🚪' },
  CA: { word: 'Casa', emoji: '🏠' },
  CO: { word: 'Conejo', emoji: '🐰' },
  CU: { word: 'Cuna', emoji: '🛏️' },
  TA: { word: 'Taza', emoji: '☕' },
  TE: { word: 'Tenedor', emoji: '🍴' },
  TI: { word: 'Tigre', emoji: '🐯' },
  TO: { word: 'Tomate', emoji: '🍅' },
  TU: { word: 'Tucán', emoji: '🦜' },
  LA: { word: 'Lápiz', emoji: '✏️' },
  LE: { word: 'León', emoji: '🦁' },
  LI: { word: 'Limón', emoji: '🍋' },
  LO: { word: 'Loro', emoji: '🦜' },
  LU: { word: 'Luna', emoji: '🌙' },
  SA: { word: 'Sapo', emoji: '🐸' },
  SE: { word: 'Serpiente', emoji: '🐍' },
  SI: { word: 'Silla', emoji: '🪑' },
  SO: { word: 'Sol', emoji: '☀️' },
  SU: { word: 'Suelo', emoji: '🌱' },
  BA: { word: 'Barco', emoji: '⛵' },
  BE: { word: 'Bebé', emoji: '👶' },
  BI: { word: 'Bici', emoji: '🚲' },
  BO: { word: 'Botón', emoji: '🔘' },
  BU: { word: 'Búho', emoji: '🦉' },
  DA: { word: 'Dado', emoji: '🎲' },
  DE: { word: 'Dedo', emoji: '☝️' },
  DI: { word: 'Diente', emoji: '🦷' },
  DO: { word: 'Dominó', emoji: '🀫' },
  DU: { word: 'Durazno', emoji: '🍑' },
  FA: { word: 'Faro', emoji: '🗼' },
  FE: { word: 'Feliz', emoji: '😊' },
  FI: { word: 'Fiesta', emoji: '🎉' },
  FO: { word: 'Foca', emoji: '🦭' },
  FU: { word: 'Fuego', emoji: '🔥' },
  GA: { word: 'Gato', emoji: '🐱' },
  GO: { word: 'Gota', emoji: '💧' },
  GU: { word: 'Gusano', emoji: '🐛' },
  JA: { word: 'Jabón', emoji: '🧼' },
  JE: { word: 'Jefe', emoji: '🧑‍💼' },
  JI: { word: 'Jirafa', emoji: '🦒' },
  JO: { word: 'Joya', emoji: '💎' },
  JU: { word: 'Jugo', emoji: '🧃' },
  NA: { word: 'Naranja', emoji: '🍊' },
  NE: { word: 'Nene', emoji: '👦' },
  NI: { word: 'Nido', emoji: '🪹' },
  NO: { word: 'Noche', emoji: '🌙' },
  NU: { word: 'Nube', emoji: '☁️' },
  RA: { word: 'Rana', emoji: '🐸' },
  RE: { word: 'Reloj', emoji: '⏰' },
  RI: { word: 'Río', emoji: '🌊' },
  RO: { word: 'Rosa', emoji: '🌹' },
  RU: { word: 'Rueda', emoji: '🛞' },
  VA: { word: 'Vaca', emoji: '🐮' },
  VE: { word: 'Vela', emoji: '🕯️' },
  VI: { word: 'Viento', emoji: '💨' },
  VO: { word: 'Volcán', emoji: '🌋' },
  VU: { word: 'Vuelo', emoji: '✈️' },
  ZA: { word: 'Zapato', emoji: '👟' },
  ZE: { word: 'Zeta', emoji: '⚡' },
  ZI: { word: 'Zigzag', emoji: '〰️' },
  ZO: { word: 'Zorro', emoji: '🦊' },
  ZU: { word: 'Zumo', emoji: '🧃' }
};
